/**
 * Z-Index Layer Management
 * 
 * Centralized z-index scale to prevent overlay conflicts and z-index wars.
 * All navigation, modals, and overlays must use these defined layers.
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

// ===============================
// Z-INDEX SCALE
// ===============================

/**
 * Standardized z-index layers for consistent stacking
 * 
 * Lower numbers = behind
 * Higher numbers = in front
 * 
 * Gaps between values allow for future additions without refactoring
 */
export const Z_LAYERS = {
  // Base navigation
  bottomNav: 20,        // Mobile tab bar - always on screen
  chatInput: 25,        // Chat input bar - glued to top of bottomNav
  topBar: 30,          // App header / PublicHeader
  
  // Interactive overlays
  dropdown: 40,        // Small dropdowns, tooltips
  overlay: 50,         // MobileContextMenu, drawer overlays
  
  // Modal hierarchy  
  modal: 60,           // Standard modals
  toast: 70,           // Toast notifications - highest priority
} as const;

// ===============================
// UTILITY FUNCTIONS
// ===============================

/**
 * Generate Tailwind z-index class from layer
 * Usage: className={getZIndexClass('overlay')}
 */
export function getZIndexClass(layer: keyof typeof Z_LAYERS): string {
  return `z-[${Z_LAYERS[layer]}]`;
}

/**
 * Get raw z-index value for CSS-in-JS or style props
 * Usage: style={{ zIndex: getZIndexValue('modal') }}
 */
export function getZIndexValue(layer: keyof typeof Z_LAYERS): number {
  return Z_LAYERS[layer];
}

/**
 * Check if one layer is above another
 * Useful for conditional rendering logic
 */
export function isLayerAbove(layer1: keyof typeof Z_LAYERS, layer2: keyof typeof Z_LAYERS): boolean {
  return Z_LAYERS[layer1] > Z_LAYERS[layer2];
}

// ===============================
// TYPE EXPORTS
// ===============================

export type ZLayer = keyof typeof Z_LAYERS;

// ===============================
// USAGE EXAMPLES
// ===============================

/*
// Tailwind classes
<div className={getZIndexClass('overlay')}>
  Mobile menu content
</div>

// Inline styles
<div style={{ zIndex: getZIndexValue('modal') }}>
  Modal backdrop
</div>

// Conditional logic
if (isLayerAbove('modal', 'overlay')) {
  // Modal will appear above overlay
}
*/